import { GraduationCap, Settings2 } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAsync } from "@/hooks/use-async";
import { useAdminCourse, useAdminKey } from "@/hooks/use-auth";
import { adminApi } from "@/lib/api";
import { adminCourseStore } from "@/lib/auth";

/**
 * Which course the scoped admin views are acting on.
 *
 * Purely a selector. Creating and retiring terms and courses lives on the Terms &
 * courses page, because those are the objects this control chooses *between* —
 * an earlier version combined the two and the result was a dropdown that both
 * scoped the page and created new records, which is two jobs in one widget.
 *
 * Rendered only above the views that are actually course-scoped, so the
 * distinction between "acting within a course" and "managing the courses
 * themselves" is visible rather than something you have to infer.
 */
export function CourseScope() {
  const adminKey = useAdminKey();
  const selected = useAdminCourse();

  const terms = useAsync(adminKey ? () => adminApi.terms(adminKey) : null, [adminKey]);
  const courses = useAsync(
    adminKey ? () => adminApi.courses(adminKey) : null,
    [adminKey],
  );

  // Choose a sensible default, and recover if the stored course was deleted —
  // otherwise every scoped view sits empty with no visible cause.
  useEffect(() => {
    const list = courses.data;
    if (!list?.length) return;
    if (selected && list.some((c) => c.id === selected)) return;
    adminCourseStore.set((list.find((c) => c.active) ?? list[0]).id);
  }, [courses.data, selected]);

  if (courses.loading) return <Skeleton className="h-9 w-72" />;

  const list = courses.data ?? [];
  if (!list.length) {
    // A fresh deployment. Without this, three admin pages look broken rather than
    // unconfigured.
    return (
      <p className="text-muted-foreground text-sm">
        No courses yet.{" "}
        <Link to="/admin/terms" className="text-foreground underline underline-offset-4">
          Create a term and a course
        </Link>{" "}
        to begin.
      </p>
    );
  }

  const termById = new Map((terms.data ?? []).map((t) => [t.id, t]));
  const current = list.find((c) => c.id === selected);
  const currentTerm = current ? termById.get(current.term_id) : undefined;
  const retired = current ? !current.active || currentTerm?.active === false : false;

  // The trigger label for each course id. Base UI's Select.Value renders the raw
  // value unless the Root is given this map, so without it the trigger read
  // "2026-spring-rob-uy-3203" while the dropdown looked correct. Built from the
  // same list that renders the items below.
  const labels = Object.fromEntries(
    list.map((course) => [
      course.id,
      `${course.code} · ${termById.get(course.term_id)?.label ?? course.term_id}`,
    ]),
  );

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span className="text-muted-foreground text-sm">Course</span>

      <Select
        items={labels}
        value={selected ?? ""}
        onValueChange={(value) => value && adminCourseStore.set(value)}
      >
        <SelectTrigger className="w-[18rem]" aria-label="Course">
          <GraduationCap className="size-4 shrink-0" aria-hidden />
          <SelectValue placeholder="Select a course" />
        </SelectTrigger>
        <SelectContent>
          {list.map((course) => {
            const term = termById.get(course.term_id);
            return (
              <SelectItem key={course.id} value={course.id}>
                <span className="flex items-baseline gap-2">
                  <span className="font-medium">{course.code}</span>
                  <span className="text-muted-foreground text-xs">
                    {term?.label ?? course.term_id}
                  </span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {retired ? (
        <Badge variant="secondary" className="gap-1">
          Retired — keys inactive
        </Badge>
      ) : null}

      <Link
        to="/admin/terms"
        className="text-muted-foreground hover:text-foreground ml-auto inline-flex items-center gap-1.5 text-sm underline-offset-4 hover:underline"
      >
        <Settings2 className="size-3.5" aria-hidden />
        Manage terms
      </Link>
    </div>
  );
}
